import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import {
  buildChartSeries,
  buildEnergyExpenditureStats,
  buildMacroBreakdownSeries,
  buildWeekdayAdherenceSeries,
  toUtcNumber,
} from "../../../../../utils/nutrition-progress";
import { ReportsIntakeChart } from "./ReportsIntakeChart";
import ReportsMacroBreakdown from "./ReportsMacroBreakdown";
import { ReportsWeekdayChart } from "./ReportsWeekdayChart";
import { ReportsOverUnderChart } from "./ReportsOverUnderChart";

// Reports view — trend charts: rolling intake vs weight, macro stack,
// weekday adherence, over/under-by-weekday. All 4 charts + subphases done —
// see plan §24 for the over/under chart's own build history.

const rt = (t, key, opts) => t(`nutritionPage.progress.reports.${key}`, opts);

const ReportsView = ({ dayRows = [], tdeeDayRows = [], units = "metric", tdeeOverride = null }) => {
  const { t } = useTranslation("athlete-portal");

  // Same functions SummaryView.js already uses for its own Hero chart — see
  // analysis.js. No new data plumbing for the intake chart. `tdeeDayRows`
  // (always trailing 3 months, NOT dayRows) + `tdeeOverride` are both
  // resolved once, up in NutritionProgress.js, and handed down here AND to
  // SummaryView.js — so this chart's TDEE reference line can never show a
  // different number than Summary's (see NutritionProgress.js's own comment
  // on tdeeDayRows/tdeeOverrideState for the full "why").
  const series = useMemo(() => buildChartSeries(dayRows), [dayRows]);
  const expenditure = useMemo(
    () => buildEnergyExpenditureStats(tdeeDayRows, tdeeOverride),
    [tdeeDayRows, tdeeOverride]
  );
  // "Avg" reuses nutritionPage.chart.avg — the legacy AdherenceChartNew.js's
  // own average-column label, same word this chart's avg bar needs.
  const macroSeries = useMemo(
    () => buildMacroBreakdownSeries(dayRows, t("nutritionPage.chart.avg")),
    [dayRows, t]
  );
  // "Show Averages only" variant (per-ISO-week bars) — see ReportsMacroBreakdown.
  const macroWeeklySeries = useMemo(
    () => buildMacroBreakdownSeries(dayRows, t("nutritionPage.chart.avg"), { weekly: true }),
    [dayRows, t]
  );
  const weekdaySeries = useMemo(() => buildWeekdayAdherenceSeries(dayRows), [dayRows]);

  // Same "today" convention SummaryView.js uses (see its own comment) — a
  // local useMemo rather than a prop, since todayUtc isn't currently
  // threaded down from NutritionProgress.js to any of the 5 views. Passed
  // straight to ReportsOverUnderChart, which owns its own metric-toggle
  // state and computes buildOverUnderSeries itself (see that component's
  // own header comment for why the series isn't computed here like the
  // sibling charts' fixed series are).
  const todayUtc = useMemo(() => toUtcNumber(new Date()), []);

  // workingExpenditure can be non-null even when !confident (a manual
  // override with no data yet, PRD feedback — see buildEnergyExpenditureStats'
  // own comment) — gate on the value itself, not on `confident`, so the
  // reference line shows in that case too.
  const workingExpenditure = expenditure.workingExpenditure;
  const decoupled = expenditure.confident && expenditure.decoupled;
  const staleData = expenditure.confident && expenditure.staleData;
  const weeklyChange = expenditure.confident ? expenditure.weeklyChange : null;

  return (
    <div className="np-reports">
      <div className="np-reports__card np-reports__card--intake">
        <h3 className="np-reports__card-title">{rt(t, "intakeChart.title")}</h3>
        <ReportsIntakeChart
          series={series}
          workingExpenditure={workingExpenditure}
          decoupled={decoupled}
          staleData={staleData}
          weeklyChange={weeklyChange}
          units={units}
        />
      </div>

      <div className="np-reports__card">
        <h3 className="np-reports__card-title">{rt(t, "macroChart.title")}</h3>
        <ReportsMacroBreakdown dayRows={dayRows} series={macroSeries} weeklySeries={macroWeeklySeries} />
      </div>

      <div className="np-reports__card">
        <h3 className="np-reports__card-title">{rt(t, "weekdayChart.title")}</h3>
        <p className="np-reports__card-subtitle">{rt(t, "weekdayChart.subtitle")}</p>
        <ReportsWeekdayChart series={weekdaySeries} />
      </div>

      <div className="np-reports__card">
        <h3 className="np-reports__card-title">{rt(t, "overUnderChart.title")}</h3>
        <p className="np-reports__card-subtitle">{rt(t, "overUnderChart.subtitle")}</p>
        <ReportsOverUnderChart dayRows={dayRows} todayUtc={todayUtc} />
      </div>
    </div>
  );
};

ReportsView.propTypes = {
  dayRows: PropTypes.array,
  tdeeDayRows: PropTypes.array,
  units: PropTypes.string,
  tdeeOverride: PropTypes.object,
};

export default ReportsView;
